import { Injectable } from '@angular/core';
import { EnvService, ApiHttpWrapperService } from '..';
import { Observable } from 'rxjs';
import {
  ApiAddressOrdersRequest,
  ApiAddressOrdersResponse,
  ApiCopayRequest,
  ApiCopayResponse,
  ApiCreatePaymentMethodRequest,
  ApiCreateTestPatientNotificationRequest,
  ApiCreateToDoRequest,
  ApiDatesOfServiceRequest,
  ApiDatesOfServiceResponse,
  ApiDaysOnHandRequiredRequest,
  ApiDaysOnHandRequiredResponse,
  ApiEditPaymentMethodRequest,
  ApiFrequenciesRequiredRequest,
  ApiFrequenciesRequiredResponse,
  ApiGetPatientsResponse,
  ApiHealthConditionsResponse,
  ApiInsuranceCardItemResponse,
  ApiInsuranceTypeReportGroupsResponse,
  ApiInternalEditPaymentMethod,
  ApiLoginPatientRequest,
  ApiLoginPatientResponse,
  ApiMedicalHistoryRequest,
  ApiMedicalHistoryResponse,
  ApiNotificationItem,
  ApiOrderDetailsResponse,
  ApiPatientAddressesRequest,
  ApiPatientAddressesResponse,
  ApiPatientBalanceResponse,
  ApiPatientContactResponseItem,
  ApiPatientInsurancePolicy,
  ApiPatientInsuranceResponse,
  ApiPatientRequest,
  ApiPatientResponse,
  ApiPharmOrderResponse,
  ApiPharmOrderRequest,
  ApiPharmAddressResponse,
  ApiPaymentMethodResponse,
  ApiPaymentRequest,
  ApiProductGroupHcpcsResponse,
  ApiProviderResponse,
  ApiProviderVisitResponse,
  ApiProviderVisitsRequest,
  ApiProviderVisitValidationRequest,
  ApiProviderVisitValidationResponse,
  ApiProviderVisitVerificationRequest,
  ApiProviderVisitVerificationResponse,
  ApiRegistrationTestPatientContact,
  ApiRelationshipToInsuredResponse,
  ApiReorderDetailsResponse,
  ApiSearchUsersResponse,
  ApiSubmitReorderRequest,
  ApiSubmitPharmReorderRequest,
  ApiTestPatientRequest,
  ApiUpdatePasswordRequest,
  ApiUpdatePatientContactsAndConsentRequest,
  ApiUpdatePatientNameRequest,
  ApiUpdatePatientNameResponse,
  ApiUsStateResponse,
  IHttpResponse,
  RegistrationTestPatient,
  TestPatient,
  PwpApiOrderResponse,
  OrderStatusApiOrderResponse,
  ApiShipmentCollectionStatusResponse,
  ApiShipmentCollectionStatusRequest,
  ApiSetCgm90EnrollmentRequest,
  ApiSignInAgentWithOtpResponse,
  ApiSetTestPatientCopayExclusionRequest,
  ApiCreateTestAgentOtpRequest,
  ApiTestAgentOtpResponse,
  ApiSetTestPatientDeductibleOverridesRequest,
  ApiReleaseShipmentRequest,
} from '../../models';
import { urls } from './api-urls';

@Injectable({ providedIn: 'root' })
export class ApiService {
  constructor(private envService: EnvService, private httpClient: ApiHttpWrapperService) {}

  signInAgentWithOtp(otp: string): Observable<IHttpResponse<ApiSignInAgentWithOtpResponse>> {
    return this.httpClient.get<IHttpResponse<ApiSignInAgentWithOtpResponse>>(
      urls['signInAgentWithOtp'].endpoint(this.envService, { otp })
    );
  }

  createTestAgentOtp(
    body: ApiCreateTestAgentOtpRequest,
    agentToken: string
  ): Observable<IHttpResponse<ApiTestAgentOtpResponse>> {
    const options = {
      headers: { Authorization: agentToken },
    };

    return this.httpClient.put<IHttpResponse<ApiTestAgentOtpResponse>>(
      urls['createTestAgentOtp'].endpoint(this.envService),
      body,
      options
    );
  }

  getTestPatients(body: ApiTestPatientRequest): Observable<IHttpResponse<TestPatient[]>> {
    // Always use a hardcoded basic token to fetch test patients
    const options = {
      headers: { Authorization: this.envService.test.basicToken },
    };

    return this.httpClient.put<IHttpResponse<TestPatient[]>>(
      urls['getTestPatients'].endpoint(this.envService),
      body,
      options
    );
  }

  resetTestPatientPaymentMethods(patientId: number): Observable<IHttpResponse<{}>> {
    // Always use a hardcoded basic token to fetch test patients
    const options = {
      headers: { Authorization: this.envService.test.basicToken },
    };

    return this.httpClient.delete<IHttpResponse<{}>>(
      urls['resetTestPatientPaymentMethods'].endpoint(this.envService, { patientId }),
      options
    );
  }

  expireTestPatientPaymentMethod(
    patientId: number,
    paymentMethodId: string,
    body: ApiInternalEditPaymentMethod
  ): Observable<IHttpResponse<{}>> {
    // Always use a hardcoded basic token to fetch test patients
    const options = {
      headers: { Authorization: this.envService.test.basicToken },
    };

    return this.httpClient.patch<IHttpResponse<{}>>(
      urls['expireTestPatientPaymentMethod'].endpoint(this.envService, { patientId, paymentMethodId }),
      body,
      options
    );
  }

  getRegistrationTestPatients(): Observable<IHttpResponse<RegistrationTestPatient[]>> {
    // Always use a hardcoded basic token to fetch test patients
    const options = {
      headers: { Authorization: this.envService.test.basicToken },
    };

    return this.httpClient.get<IHttpResponse<RegistrationTestPatient[]>>(
      urls['getRegistrationTestPatients'].endpoint(this.envService),
      options
    );
  }

  updateRegistrationTestPatientContacts(body: ApiRegistrationTestPatientContact): Observable<IHttpResponse<{}>> {
    // Always use a hardcoded basic token to fetch test patients
    const options = {
      headers: { Authorization: this.envService.test.basicToken },
    };

    return this.httpClient.put<IHttpResponse<{}>>(
      urls['updateRegistrationTestPatientContacts'].endpoint(this.envService),
      body,
      options
    );
  }

  clearTestPatientNotifications(patientId: number): Observable<IHttpResponse<{}>> {
    // Always use a hardcoded basic token to fetch test patients
    const options = {
      headers: { Authorization: this.envService.test.basicToken },
    };

    return this.httpClient.delete<IHttpResponse<{}>>(
      urls['clearTestPatientNotifications'].endpoint(this.envService, { patientId }),
      options
    );
  }

  createTestPatientNotification(
    body: ApiCreateTestPatientNotificationRequest,
    patientId: number
  ): Observable<IHttpResponse<{}>> {
    // Always use a hardcoded basic token to fetch test patients
    const options = {
      headers: { Authorization: this.envService.test.basicToken },
    };

    return this.httpClient.post<IHttpResponse<{}>>(
      urls['createTestPatientNotification'].endpoint(this.envService, { patientId }),
      body,
      options
    );
  }

  setTestPatientCopayExclusion(
    body: ApiSetTestPatientCopayExclusionRequest,
    patientId: number
  ): Observable<IHttpResponse<{}>> {
    // Always use a hardcoded basic token to fetch test patients
    const options = {
      headers: { Authorization: this.envService.test.basicToken },
    };

    return this.httpClient.put<IHttpResponse<{}>>(
      urls['setTestPatientCopayExclusion'].endpoint(this.envService, { patientId }),
      body,
      options
    );
  }

  setTestPatientDeductibleOverrides(
    body: ApiSetTestPatientDeductibleOverridesRequest,
    patientId: number
  ): Observable<IHttpResponse<{}>> {
    // Always use a hardcoded basic token to fetch test patients
    const options = {
      headers: { Authorization: this.envService.test.basicToken },
    };

    return this.httpClient.put<IHttpResponse<{}>>(
      urls['setTestPatientDeductibleOverrides'].endpoint(this.envService, { patientId }),
      body,
      options
    );
  }

  getPatients(): Observable<IHttpResponse<ApiGetPatientsResponse>> {
    return this.httpClient.get<IHttpResponse<ApiGetPatientsResponse>>(
      urls['getPatients'].endpoint(this.envService),
      {}
    );
  }

  loginPatient(body: ApiLoginPatientRequest): Observable<IHttpResponse<ApiLoginPatientResponse>> {
    return this.httpClient.post<IHttpResponse<ApiLoginPatientResponse>>(
      urls['loginPatient'].endpoint(this.envService),
      body
    );
  }

  revokePreviousTokens(): Observable<IHttpResponse<{}>> {
    return this.httpClient.get<IHttpResponse<{}>>(urls['revokePreviousTokens'].endpoint(this.envService));
  }

  getPatient(patientId: number): Observable<IHttpResponse<ApiPatientResponse[]>> {
    return this.httpClient.get<IHttpResponse<ApiPatientResponse[]>>(
      urls['getPatient'].endpoint(this.envService, { patientId })
    );
  }

  getPharmOrders(patientId: number): Observable<IHttpResponse<ApiPharmOrderResponse[]>> {
    return this.httpClient.get<IHttpResponse<ApiPharmOrderResponse[]>>(
      urls['getPharmOrders'].endpoint(this.envService, { patientId })
    );
  }

  getPharmAddress(
    pharmacy: string,
    patientId: number,
    customerAddressId: string
  ): Observable<IHttpResponse<ApiPharmAddressResponse[]>> {
    return this.httpClient.get<IHttpResponse<ApiPharmAddressResponse[]>>(
      urls['getPharmAddress'].endpoint(this.envService, { pharmacy, patientId, customerAddressId })
    );
  }

  getPwpOrders(patientId: number): Observable<IHttpResponse<PwpApiOrderResponse[]>> {
    return this.httpClient.get<IHttpResponse<PwpApiOrderResponse[]>>(
      `${urls['getPwpOrders'].endpoint(this.envService, { patientId })}`
    );
  }

  getOrderStatusOrders(patientId: number): Observable<IHttpResponse<OrderStatusApiOrderResponse[]>> {
    return this.httpClient.get<IHttpResponse<OrderStatusApiOrderResponse[]>>(
      `${urls['getOrderStatusOrders'].endpoint(this.envService, { patientId })}?filterDups=true`
    );
  }

  getOrderDetails(patientId: number, orderId: number): Observable<IHttpResponse<ApiOrderDetailsResponse>> {
    return this.httpClient.get<IHttpResponse<ApiOrderDetailsResponse>>(
      urls['getOrderDetails'].endpoint(this.envService, { patientId, orderId }),
      { params: { includeInsurance: 'true' } }
    );
  }

  getReorderDetails(patientId: number, reorderId: number): Observable<IHttpResponse<ApiReorderDetailsResponse[]>> {
    return this.httpClient.get<IHttpResponse<ApiReorderDetailsResponse[]>>(
      urls['getReorderDetails'].endpoint(this.envService, { patientId, reorderId }),
      { params: { validateQuantities: 'true' } }
    );
  }

  getShipmentDetails(patientId: number, shipmentId: number): Observable<IHttpResponse<ApiReorderDetailsResponse[]>> {
    return this.httpClient.get<IHttpResponse<ApiReorderDetailsResponse[]>>(
      urls['getShipmentDetails'].endpoint(this.envService, { patientId, shipmentId }),
      { params: { validateQuantities: 'true' } }
    );
  }

  getShipmentCollectionStatus(
    patientId: number,
    shipmentId: number
  ): Observable<IHttpResponse<ApiShipmentCollectionStatusResponse>> {
    return this.httpClient.get<IHttpResponse<ApiShipmentCollectionStatusResponse>>(
      urls['getShipmentCollectionStatus'].endpoint(this.envService, { patientId, shipmentId })
    );
  }

  setShipmentCollectionStatus(
    patientId: number,
    shipmentId: number,
    body: ApiShipmentCollectionStatusRequest
  ): Observable<IHttpResponse<{}>> {
    return this.httpClient.put<IHttpResponse<{}>>(
      urls['setShipmentCollectionStatus'].endpoint(this.envService, { patientId, shipmentId }),
      body
    );
  }

  releaseShipment(body: ApiReleaseShipmentRequest): Observable<IHttpResponse<{}>> {
    const patientId = body.patientId;
    return this.httpClient.put<IHttpResponse<{}>>(
      urls['releaseShipment'].endpoint(this.envService, { patientId }),
      body
    );
  }

  getDaysOnHandRequired(
    patientId: number,
    body: ApiDaysOnHandRequiredRequest
  ): Observable<IHttpResponse<ApiDaysOnHandRequiredResponse[]>> {
    return this.httpClient.put<IHttpResponse<ApiDaysOnHandRequiredResponse[]>>(
      urls['getDaysOnHandRequired'].endpoint(this.envService, { patientId }),
      body
    );
  }

  getFrequenciesRequired(
    patientId: number,
    body: ApiFrequenciesRequiredRequest
  ): Observable<IHttpResponse<ApiFrequenciesRequiredResponse[]>> {
    return this.httpClient.put<IHttpResponse<ApiFrequenciesRequiredResponse[]>>(
      urls['getFrequenciesRequired'].endpoint(this.envService, { patientId }),
      body
    );
  }

  getRelationshipsToInsured(): Observable<IHttpResponse<ApiRelationshipToInsuredResponse[]>> {
    return this.httpClient.get<IHttpResponse<ApiRelationshipToInsuredResponse[]>>(
      urls['getRelationshipsToInsured'].endpoint(this.envService)
    );
  }

  getPatientInsurance(patientId: number): Observable<IHttpResponse<ApiPatientInsuranceResponse[]>> {
    return this.httpClient.get<IHttpResponse<ApiPatientInsuranceResponse[]>>(
      urls['getPatientInsurance'].endpoint(this.envService, { patientId })
    );
  }

  getProviders(patientId: number, shipmentTypeId: number): Observable<IHttpResponse<ApiProviderResponse[]>> {
    return this.httpClient.get<IHttpResponse<ApiProviderResponse[]>>(
      urls['getProviders'].endpoint(this.envService, { patientId, shipmentTypeId })
    );
  }

  getProviderVisitVerification(
    patientId: number,
    requestBody: ApiProviderVisitVerificationRequest
  ): Observable<IHttpResponse<ApiProviderVisitVerificationResponse[]>> {
    return this.httpClient.put<IHttpResponse<ApiProviderVisitVerificationResponse[]>>(
      urls['getProviderVisitVerification'].endpoint(this.envService, { patientId }),
      requestBody
    );
  }

  validateProviderVisit(
    patientId: number,
    requestBody: ApiProviderVisitValidationRequest
  ): Observable<IHttpResponse<ApiProviderVisitValidationResponse>> {
    return this.httpClient.put<IHttpResponse<ApiProviderVisitValidationResponse>>(
      urls['validateProviderVisit'].endpoint(this.envService, { patientId }),
      requestBody
    );
  }

  getProviderVisits(
    patientId: number,
    requestBody: ApiProviderVisitsRequest
  ): Observable<IHttpResponse<ApiProviderVisitResponse[]>> {
    return this.httpClient.put<IHttpResponse<ApiProviderVisitResponse[]>>(
      urls['getProviderVisits'].endpoint(this.envService, { patientId }),
      requestBody
    );
  }

  getUsStates(): Observable<IHttpResponse<ApiUsStateResponse[]>> {
    return this.httpClient.get<IHttpResponse<ApiUsStateResponse[]>>(urls['getUsStates'].endpoint(this.envService));
  }

  getAddresses(patientId: number): Observable<IHttpResponse<ApiPatientAddressesResponse[]>> {
    return this.httpClient.get<IHttpResponse<ApiPatientAddressesResponse[]>>(
      urls['getAddresses'].endpoint(this.envService, { patientId })
    );
  }

  getCopay(requestBody: ApiCopayRequest): Observable<IHttpResponse<ApiCopayResponse>> {
    return this.httpClient.post<IHttpResponse<ApiCopayResponse>>(
      urls['getCopay'].endpoint(this.envService),
      requestBody
    );
  }

  getDatesOfService(body: ApiDatesOfServiceRequest): Observable<IHttpResponse<ApiDatesOfServiceResponse>> {
    return this.httpClient.put<IHttpResponse<ApiDatesOfServiceResponse>>(
      urls['getDatesOfService'].endpoint(this.envService),
      body
    );
  }

  getPaymentMethods(patientId: number): Observable<IHttpResponse<ApiPaymentMethodResponse[]>> {
    return this.httpClient.get<IHttpResponse<ApiPaymentMethodResponse[]>>(
      urls['getPaymentMethods'].endpoint(this.envService, { patientId })
    );
  }

  editPaymentMethod(
    patientId: number,
    paymentMethodId: string,
    body: ApiEditPaymentMethodRequest
  ): Observable<IHttpResponse<ApiPaymentMethodResponse>> {
    return this.httpClient.patch<IHttpResponse<ApiPaymentMethodResponse>>(
      urls['editPaymentMethod'].endpoint(this.envService, { patientId, paymentMethodId }),
      body
    );
  }

  createPaymentMethod(
    patientId: number,
    body: ApiCreatePaymentMethodRequest,
    basicAuth: boolean = false
  ): Observable<IHttpResponse<ApiPaymentMethodResponse>> {
    const options = {
      headers: { Authorization: this.envService.test.basicToken },
    };

    return this.httpClient.post<IHttpResponse<ApiPaymentMethodResponse>>(
      urls['createPaymentMethod'].endpoint(this.envService, { patientId }),
      body,
      basicAuth ? options : undefined
    );
  }

  getProductGroupHcpcs(hcpcGroupName: string): Observable<IHttpResponse<ApiProductGroupHcpcsResponse>> {
    return this.httpClient.get<IHttpResponse<ApiProductGroupHcpcsResponse>>(
      urls['getProductGroupHcpcs'].endpoint(this.envService, { hcpcGroupName })
    );
  }

  getInsuranceTypeReportGroups(
    insuranceTypeName: string
  ): Observable<IHttpResponse<ApiInsuranceTypeReportGroupsResponse>> {
    return this.httpClient.get<IHttpResponse<ApiInsuranceTypeReportGroupsResponse>>(
      urls['getInsuranceTypeReportGroups'].endpoint(this.envService, { insuranceTypeName })
    );
  }

  submitReorder(body: ApiSubmitReorderRequest): Observable<IHttpResponse<{}>> {
    return this.httpClient.put<IHttpResponse<{}>>(urls['submitReorder'].endpoint(this.envService), body);
  }

  submitPharmReorder(body: ApiSubmitPharmReorderRequest): Observable<IHttpResponse<{}>> {
    return this.httpClient.put<IHttpResponse<{}>>(urls['submitPharmReorder'].endpoint(this.envService), body);
  }

  submitReorderForAgent(body: ApiSubmitReorderRequest): Observable<IHttpResponse<{}>> {
    return this.httpClient.put<IHttpResponse<{}>>(urls['submitReorderForAgent'].endpoint(this.envService), body);
  }

  submitShipment(body: ApiSubmitReorderRequest): Observable<IHttpResponse<{}>> {
    return this.httpClient.put<IHttpResponse<{}>>(urls['submitShipment'].endpoint(this.envService), body);
  }

  makePayment(body: ApiPaymentRequest, patientId: number): Observable<IHttpResponse<{}>> {
    return this.httpClient.post<IHttpResponse<{}>>(urls['makePayment'].endpoint(this.envService, { patientId }), body);
  }

  updatePatientAddresses(
    patientId: number,
    body: ApiPatientAddressesRequest
  ): Observable<IHttpResponse<ApiPatientAddressesResponse[]>> {
    return this.httpClient.put<IHttpResponse<ApiPatientAddressesResponse[]>>(
      urls['updatePatientAddresses'].endpoint(this.envService, { patientId }),
      body
    );
  }

  createToDo(body: ApiCreateToDoRequest): Observable<IHttpResponse<{}>> {
    return this.httpClient.post<IHttpResponse<{}>>(urls['createToDo'].endpoint(this.envService), body);
  }

  updatePatient(
    patientId: number,
    body: ApiPatientRequest,
    basicAuth?: boolean
  ): Observable<IHttpResponse<ApiPatientResponse[]>> {
    let options = {};
    if (basicAuth) {
      options = {
        headers: { Authorization: this.envService.test.basicToken },
      };
    }
    return this.httpClient.post<IHttpResponse<ApiPatientResponse[]>>(
      urls['updatePatient'].endpoint(this.envService, { patientId }),
      body,
      options
    );
  }

  updatePharmOrder(patientId: number, body: ApiPharmOrderRequest): Observable<IHttpResponse<ApiPharmOrderResponse[]>> {
    return this.httpClient.post<IHttpResponse<ApiPharmOrderResponse[]>>(
      urls['updatePharmOrder'].endpoint(this.envService, { patientId }),
      body
    );
  }

  createPatientInsurance(patientId: number, body: ApiPatientInsurancePolicy[]): Observable<IHttpResponse<{}>> {
    return this.httpClient.post<IHttpResponse<{}>>(
      urls['createPatientInsurance'].endpoint(this.envService, { patientId }),
      body
    );
  }

  getPatientContacts(patientId: number): Observable<IHttpResponse<ApiPatientContactResponseItem[]>> {
    return this.httpClient.get(urls['getPatientContacts'].endpoint(this.envService, { patientId }));
  }

  updatePatientName(
    patientId: number,
    body: ApiUpdatePatientNameRequest
  ): Observable<IHttpResponse<ApiUpdatePatientNameResponse[]>> {
    return this.httpClient.put<IHttpResponse<ApiUpdatePatientNameResponse[]>>(
      urls['updatePatientName'].endpoint(this.envService, { patientId }),
      body
    );
  }

  updatePatientContactsAndConsent(
    patientId: number,
    body: ApiUpdatePatientContactsAndConsentRequest
  ): Observable<IHttpResponse<ApiPatientContactResponseItem[]>> {
    return this.httpClient.put<IHttpResponse<ApiPatientContactResponseItem[]>>(
      urls['updatePatientContactsAndConsent'].endpoint(this.envService, { patientId }),
      body
    );
  }

  getPatientInsuranceCards(patientId: number): Observable<IHttpResponse<ApiInsuranceCardItemResponse[]>> {
    return this.httpClient.get<IHttpResponse<ApiInsuranceCardItemResponse[]>>(
      urls['getPatientInsuranceCards'].endpoint(this.envService, { patientId })
    );
  }

  updatePassword(body: ApiUpdatePasswordRequest): Observable<IHttpResponse<{}>> {
    return this.httpClient.post<IHttpResponse<{}>>(urls['updatePassword'].endpoint(this.envService), body);
  }

  getMedicalHistory(patientId: number): Observable<IHttpResponse<ApiMedicalHistoryResponse>> {
    return this.httpClient.get<IHttpResponse<ApiMedicalHistoryResponse>>(
      urls['getMedicalHistory'].endpoint(this.envService, { patientId })
    );
  }

  getHealthConditions(): Observable<IHttpResponse<ApiHealthConditionsResponse>> {
    return this.httpClient.get<IHttpResponse<ApiHealthConditionsResponse>>(
      urls['getHealthConditions'].endpoint(this.envService)
    );
  }

  updateMedicalHistory(
    patientId: number,
    body: ApiMedicalHistoryRequest
  ): Observable<IHttpResponse<ApiMedicalHistoryResponse>> {
    return this.httpClient.put<IHttpResponse<ApiMedicalHistoryResponse>>(
      urls['updateMedicalHistory'].endpoint(this.envService, { patientId }),
      body
    );
  }

  searchUsers(patientId: number): Observable<IHttpResponse<ApiSearchUsersResponse[]>> {
    return this.httpClient.get<IHttpResponse<ApiSearchUsersResponse[]>>(
      `${urls['searchUsers'].endpoint(this.envService)}?patientId=${patientId}`
    );
  }

  getAddressOrders(patientId: number, addressId: number): Observable<IHttpResponse<ApiAddressOrdersResponse>> {
    return this.httpClient.get<IHttpResponse<ApiAddressOrdersResponse>>(
      `${urls['getAddressOrders'].endpoint(this.envService, { patientId })}?addressId=${addressId}`
    );
  }

  updateAddressOrders(patientId: number, body: ApiAddressOrdersRequest): Observable<IHttpResponse<{}>> {
    return this.httpClient.put<IHttpResponse<{}>>(
      urls['updateAddressOrders'].endpoint(this.envService, { patientId }),
      body
    );
  }

  getPatientBalance(patientId: number): Observable<IHttpResponse<ApiPatientBalanceResponse>> {
    return this.httpClient.get<IHttpResponse<ApiPatientBalanceResponse>>(
      urls['getPatientBalance'].endpoint(this.envService, { patientId })
    );
  }

  getNotifications(patientId: number): Observable<IHttpResponse<ApiNotificationItem[]>> {
    return this.httpClient.put<IHttpResponse<ApiNotificationItem[]>>(
      urls['getNotifications'].endpoint(this.envService, { patientId }),
      {}
    );
  }

  respondToNotification(
    patientId: number,
    notificationId: number,
    responseId: number,
    textResponse?: string
  ): Observable<IHttpResponse<{}>> {
    return this.httpClient.put<IHttpResponse<{}>>(
      urls['respondToNotification'].endpoint(this.envService, { patientId, notificationId, responseId }),
      { textResponse }
    );
  }

  setCgm90Enrollment(patientId: number, body: ApiSetCgm90EnrollmentRequest): Observable<IHttpResponse<{}>> {
    return this.httpClient.put<IHttpResponse<{}>>(
      urls['setCgm90Enrollment'].endpoint(this.envService, { patientId }),
      body
    );
  }

  activateUser(): Observable<IHttpResponse<{}>> {
    return this.httpClient.post<IHttpResponse<{}>>(urls['activateUser'].endpoint(this.envService), {});
  }

  verifyActivationToken(token: string): Observable<IHttpResponse<{}>> {
    return this.httpClient.get<IHttpResponse<{}>>(urls['verifyActivationToken'].endpoint(this.envService, { token }));
  }

  resendActivationEmail(ccsActivationToken: string): Observable<IHttpResponse<{}>> {
    return this.httpClient.post<IHttpResponse<{}>>(urls['resendActivationEmail'].endpoint(this.envService), {
      ccsActivationToken,
    });
  }

  createUserFromEmail(email: string): Observable<IHttpResponse<{}>> {
    return this.httpClient.post<IHttpResponse<{}>>(urls['createUserFromEmail'].endpoint(this.envService), { email });
  }
}
